// Draws for the surfaced shader build: one per reference, each a quad over its outline's baked field (`fieldFor`, src/lib/surfaceField), with the reference's frame as a 3×3. The unmapped node rect is draw zero of the same build.
import { Geometry } from 'pixi.js'
import { rectQuad } from '~/lib/homography'
import { normalizeRef, resolveOutline } from '~/lib/outline'
import { applyTransform, stageDistanceScale } from '~/lib/outline/object'
import { rectField } from '~/lib/shaders/common'
import { coverFrame, flowFrame, referenceFrame } from '~/lib/surfaceFrames'
import { fieldFor } from '~/lib/surfaceField'
import { fieldKey } from '~/lib/surfaceField/cache'
import { flowEnds } from '~/lib/surfaceField/flow'

const logger = console.scope('shaderSurfaces')

/** The field's texture rectangle in world units: what `uFieldRect` carries (origin.xy, size.zw). */
export const fieldRect = field => [
	field.origin[0],
	field.origin[1],
	field.width * field.texel,
	field.height * field.texel,
]

/** The field's rectangle as a quad, TL TR BR BL, in field space: the geometry of a mapped draw before its placement. */
export const fieldQuad = field => {
	const [x, y, w, h] = fieldRect(field)
	return rectQuad(x, y, w, h)
}

/** What a mapped node resolves its references through: `{ objects }`, the live objects by id. Absent reads as empty: the node fails dark. */
export const toEnv = env => env ?? {}

const IDENTITY = [1, 0, 0, 0, 1, 0, 0, 0, 1]

/**
 * Where a draw's field sits on stage. No `transform` is identity, scale 1. An object's `transform` (`[a, b, c, d, tx, ty]`, field → stage) places the quad; `toField` (row-major 3×3, stage → field) is what the vertex stage samples through, and `scale` turns a field distance into stage units. Null for a singular or malformed transform.
 */
export const fieldPlacement = transform => {
	if (transform == null) return { transform: null, toField: IDENTITY, scale: 1 }
	if (!Array.isArray(transform) || transform.length !== 6 || !transform.every(Number.isFinite)) return null
	const [a, b, c, d, tx, ty] = transform
	const det = a * d - b * c
	const scale = stageDistanceScale(transform)
	if (!(Math.abs(det) > 1e-12) || !(scale > 0)) return null
	const toField = [d / det, -c / det, (c * ty - d * tx) / det, -b / det, a / det, (b * tx - a * ty) / det, 0, 0, 1]
	return { transform, toField, scale }
}

/** A field-space quad on stage. */
export const placeQuad = (quad, transform) => (transform ? quad.map(p => applyTransform(transform, p)) : quad)

/** A row-major 3×3 as GLSL's column-major `mat3`. */
export const frameUniform = m => new Float32Array([m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]])

// A picture, not a pattern: a surface's own frame crops it to cover; the stage frame stretches it like the unmapped sprite.
const fitFrame = (frame, ref, aspect) => (frame && aspect && ref.mode !== 'stage' ? coverFrame(frame, aspect) : frame)

/** A picture's fit on a flow draw's (u, v), as `[sx, sy, ox, oy]` (uv · s + o, `uFlowCover`): cropped to cover the river's `extent` (length × width), as `coverFrame` crops a linear frame. Null for a shader (no `aspect`) or a degenerate extent: the picture spans (u, v) ∈ [0, 1]². */
export const flowCover = (extent, aspect) => {
	const af = extent?.[0] / extent?.[1]
	if (!(aspect > 0) || !(af > 0) || !Number.isFinite(af)) return null
	const sx = af > aspect ? 1 : af / aspect
	const sy = af > aspect ? aspect / af : 1
	return [sx, sy, (1 - sx) / 2, (1 - sy) / 2]
}

// A drag rebuilds every frame: the same missing set warns once.
let warnedMissing = ''
let warnedFlow = ''

/** A flow field's grid cap while its shape is drafted (`drafted` on the env's objects): a full bake per drag frame halves the frame rate. Release bakes it whole. */
export const DRAFT_FLOW_SIZE = 128

const draftSize = (ref, env) => (ref.mode === 'flow' && env.objects?.drafted?.(ref.id) ? DRAFT_FLOW_SIZE : undefined)

/** `drawFlow` with the flow field it baked to decide: `{ ends, field }`, or null. buildSurfaceDraws draws that field, so a flow draw fetches it once. */
const flowDraw = (ref, outline, maxSize) => {
	if (ref.mode !== 'flow' || !outline) return null
	const ends = flowEnds({ from: ref.from, to: ref.to }, outline.segments)
	const field = ends && fieldFor(outline, ends, maxSize)
	if (ends && field?.flow) return { ends, field }
	const key = `${ref.id}:${ref.from}:${ref.to}:${outline.segments}:${!!ends}`
	if (key !== warnedFlow)
		logger.warn(ends ? 'flow field has no flow, drawn as edge' : 'flow endpoints unusable, drawn as edge', {
			id: ref.id,
			from: ref.from,
			to: ref.to,
			segments: outline.segments,
		})
	warnedFlow = key
	return null
}

/** A `mode: 'flow'` reference's endpoints, wrapped against the outline's segments (`flowEnds`): what its field bakes and keys by. Null for any other mode, for endpoints that cannot carry a flow (fewer than 4 segments, the same or adjacent segments; U5c refuses them in the UI), and for a flow field that bakes without one (`flow` false: an end chain borders no inside cell, e.g. the tip of a hairline spike): that reference draws as `edge` on the plain field. Bakes the flow field (`flowDraw`). */
export const drawFlow = (ref, outline) => flowDraw(ref, outline)?.ends ?? null

/**
 * One draw per reference that resolves: `{ index, frame, field, key, flow, cover, quad, toField, scale }`, `index` the reference's slot (a missing one does not renumber the rest), `key` the field's (what its texture is keyed by), `quad` on stage. `env` is `{ objects }`; `stage` the node's placed quad, the frame of a `mode: 'stage'` reference; `aspect` the sampled picture's, absent for a shader.
 * The frame spans the outline as drawn, placed on stage (segment `edge` from `anchors`, always stage space); the field is in the outline's own space and placed by its `transform`, so a pose change re-places the draw and never rebakes.
 * A `mode: 'flow'` reference with usable endpoints bakes its own field (`fieldFor(outline, ends)`, keyed with them), `flow` true, and its frame carries the field's extent (`flowFrame`); a picture covers it through `cover` (`flowCover`), since the frame's matrix is not read on a flow draw; otherwise it draws as `edge`.
 */
export const buildSurfaceDraws = (refs, env, stage, aspect) => {
	const resolveEnv = toEnv(env)
	const missing = []
	const draws = []
	;(refs ?? []).map(normalizeRef).forEach((ref, index) => {
		const outline = resolveOutline(ref, resolveEnv)
		const frame = outline && fitFrame(referenceFrame(ref, outline, stage), ref, aspect)
		const place = frame && fieldPlacement(outline.transform)
		const maxSize = draftSize(ref, resolveEnv)
		const flowing = place ? flowDraw(ref, outline, maxSize) : null
		const flow = flowing?.ends ?? null
		const field = place && (flowing?.field ?? fieldFor(outline, null))
		const flowed = flow && field && flowFrame(frame, field, place.scale)
		if (!field) {
			missing.push(ref.id)
			return
		}
		draws.push({
			index,
			frame: flowed || frame,
			field,
			key: fieldKey(outline, flow, flowed ? maxSize : undefined),
			flow: !!flowed,
			cover: flowed ? flowCover(flowed.extent, aspect) : null,
			quad: placeQuad(fieldQuad(field), place.transform),
			toField: place.toField,
			scale: place.scale,
		})
	})
	const missingKey = missing.join('|')
	if (missingKey !== warnedMissing && missing.length) logger.warn('surfaces missing, not rendered', missing)
	warnedMissing = missingKey
	return { draws, missing }
}

// A resize drag walks through a new size per frame: rect fields keep a few of their own, so they never push outline bakes out of the field cache.
const RECT_CACHE_MAX = 4
const rectCache = new Map()

const rectFieldFor = (width, height) => {
	const key = `${width}|${height}`
	let field = rectCache.get(key)
	if (field) rectCache.delete(key)
	else field = rectField(width, height)
	if (!field) return null
	rectCache.set(key, field)
	if (rectCache.size > RECT_CACHE_MAX) rectCache.delete(rectCache.keys().next().value)
	return field
}

/** The unmapped node: its own local rect as the one draw, framed so `vUV` is position over size. The quad is the rect itself, not the padded field, so the node's bounds are unchanged. */
export const buildRectDraws = (width, height) => {
	const quad = rectQuad(0, 0, width, height)
	const frame = referenceFrame({ mode: 'stage' }, null, quad)
	const field = frame && rectFieldFor(width, height)
	const draw = { index: 0, frame, field, key: `rect|${width}|${height}`, quad, toField: IDENTITY, scale: 1 }
	return { draws: field ? [draw] : [], missing: [] }
}

const QUAD_INDEX = [0, 1, 2, 0, 2, 3]

/** A two-triangle quad, TL TR BR BL, as the one attribute the surfaced vertex stage reads. */
export const quadGeometry = quad =>
	new Geometry({
		attributes: { aPosition: { buffer: new Float32Array(quad.flat()), format: 'float32x2' } },
		indexBuffer: new Uint32Array(QUAD_INDEX),
	})

/** Rewrite a quad geometry's corners in place — the same GPU buffer, re-uploaded. */
export const refillQuad = (geometry, quad) => {
	geometry.getBuffer('aPosition').data = new Float32Array(quad.flat())
}

/** A stable key over everything the draws depend on — the rebuild rides this, not every Redux change. An object's placement (`transform`) is in it, so a pose change re-places its draw; the field key is not changed by it, so nothing rebakes. */
export const surfaceGeometryKey = (refs, env, stage, aspect) => {
	const resolveEnv = toEnv(env)
	const list = (refs ?? []).map(normalizeRef)
	const key = list
		.map(r => {
			const outline = resolveOutline(r, resolveEnv)
			if (!outline) return `${r.id}:missing`
			const placed = outline.transform ? `#${outline.transform.join(',')}` : ''
			// A flow field is per reference: its endpoints are part of what is drawn, and so is a draft's coarser grid.
			const draft = draftSize(r, resolveEnv)
			const ends = r.mode === 'flow' ? `:${r.from}:${r.to}${draft ? `@${draft}` : ''}` : ''
			return `${r.id}:${outline.key}${placed}:${r.mode}:${r.edge}:${r.offset}${ends}`
		})
		.join('|')
	// The stage quad varies only by a shader fill's mirror sign, and frames only stage-fit content; the picture's aspect only the rest.
	const placed = list.some(r => r.mode === 'stage') ? `${key}@${stage?.flat().join(',')}` : key
	return aspect && list.some(r => r.mode !== 'stage') ? `${placed}~${aspect}` : placed
}
